import dayjs from "dayjs"
import { z } from "zod"

import { UOM_ROUNDING_PRECISION } from "@/constants/formats"
import { hasMaxDecimalPlaces } from "@/utils/forms"

export const AddPricingSchema = z
  .object({
    pricingTiersToCreate: z
      .array(
        z.object({
          unitOfMeasurement: z.object({
            id: z.string().min(1, { message: "Pricing UoM is mandatory" }),
            name: z.string().min(1, { message: "Pricing UoM is mandatory" }),
          }),
          numberOfUnits: z.string().refine((value) => value === "" || (!isNaN(Number(value)) && Number(value) > 0), {
            message: "Product Quantity must be a valid number greater than 0",
          }),
          pricePerUnit: z
            .string()
            .refine((value) => value === "" || hasMaxDecimalPlaces(value, UOM_ROUNDING_PRECISION), {
              message: `Price per Unit must be a valid price with up to ${UOM_ROUNDING_PRECISION} decimal places`,
            }),
        })
      )
      .refine(
        (tiers) => {
          // First tier is required - must have both numberOfUnits and pricePerUnit
          const firstTier = tiers[0]
          return firstTier?.numberOfUnits?.trim() && firstTier?.pricePerUnit?.trim()
        },
        {
          message: "Pricing Tier 1 is required - both Product Quantity and Price per Unit must be provided",
        }
      )
      .refine((tiers) =>
        tiers.every((tier, index) => {
          if (index === 0) return true // Skip first index, already validated above
          const hasNumberOfUnits = tier.numberOfUnits?.trim() !== ""
          const hasPricePerUnit = tier.pricePerUnit?.trim() !== ""
          // For tiers 2+: either both fields are empty (optional) or both are filled
          return (!hasNumberOfUnits && !hasPricePerUnit) || (hasNumberOfUnits && hasPricePerUnit)
        })
      ),
    effectiveDate: z.string().min(1, {
      message: "Effective date is mandatory",
    }),
    expirationDate: z.string().optional(),
    freightTerms: z.string().min(1, { message: "Freight terms is mandatory" }),
    incoterms: z.string().min(1, { message: "Incoterms is mandatory" }),
    incotermsLocationId: z.string().min(1, { message: "Incoterms Location is mandatory" }),
    sourceCompanyLocationAssociationId: z.string().optional(),
    pricingPackageId: z.string().optional(),
  })
  .refine(
    (data) => {
      return (
        !data.expirationDate ||
        (data.expirationDate &&
          data.effectiveDate &&
          dayjs(data.effectiveDate).startOf("day").isBefore(dayjs(data.expirationDate).startOf("day")))
      )
    },
    {
      message: "Valid until date must be after the effective date",
      path: ["expirationDate"],
    }
  )

export type AddPricingValues = z.infer<typeof AddPricingSchema>
